(() => {
  'use strict';

  const selectors = {
    apiTemplate: '[data-api-related-template]',
    productItem: '.product-item',
    gridItem: '[data-grid-item]',
    relatedSection: '[data-related-section]',
    recentlyViewed: 'recently-viewed',
    gridSlider: 'grid-slider',
    recentlyViewedWrapper: '[data-recently-viewed-wrapper]',
  };

  const attributes = {
    limit: 'data-limit',
    productId: 'data-product-id',
    layout: 'data-layout',
  };

  const classes = {
    hidden: 'hidden',
    mobileSlider: 'grid--mobile-slider',
    slider: 'grid--slider',
  };

  if (customElements.get('related-products')) return;

  customElements.define(
    'related-products',
    class RelatedProducts extends HTMLElement {
      constructor() {
        super();

        this.relatedItems = 0;
        this.wrapper = null;
        this.recentlyViewed = null;
        this.relatedSection = null;
        this.abortController = null;
        this.relatedObserver = null;
        this.recentlyViewedLoadedEvent = () => this.handleRecentlyViewedResponse();
      }

      connectedCallback() {
        this.wrapper = this.querySelector(selectors.recentlyViewedWrapper);
        this.recentlyViewed = this.querySelector(selectors.recentlyViewed);
        this.relatedSection = this.querySelector(selectors.relatedSection);

        this.observeRelatedProducts();
        this.loadRecentlyViewedProducts();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.relatedObserver?.disconnect();
        this.relatedObserver = null;
        this.recentlyViewed?.removeEventListener(
          'theme:recently-viewed:loaded',
          this.recentlyViewedLoadedEvent
        );
      }

      observeRelatedProducts() {
        const shouldLoadImmediately =
          !this.relatedSection ||
          window.Shopify?.designMode ||
          !('IntersectionObserver' in window);

        if (shouldLoadImmediately) {
          this.loadRelatedProducts();
          return;
        }

        this.relatedObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            this.relatedObserver?.disconnect();
            this.relatedObserver = null;
            this.loadRelatedProducts();
          },
          { rootMargin: '250px 0px', threshold: 0 }
        );

        this.relatedObserver.observe(this.relatedSection);
      }

      loadRelatedProducts() {
        if (!this.relatedSection) {
          this.updateVisibility();
          return;
        }

        const productId = this.relatedSection.getAttribute(attributes.productId);
        const limit = this.relatedSection.getAttribute(attributes.limit);
        const recommendationsRoute = window.theme?.routes?.product_recommendations_url;

        if (!productId || !limit || !recommendationsRoute) {
          this.hideSection(this.relatedSection);
          this.updateVisibility();
          return;
        }

        const requestUrl = `${recommendationsRoute}?section_id=api-product-recommendation&limit=${encodeURIComponent(
          limit
        )}&product_id=${encodeURIComponent(productId)}&intent=related`;

        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Related products request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => this.handleRelatedProductsResponse(responseText))
          .catch((error) => {
            if (error.name !== 'AbortError') this.hideSection(this.relatedSection);
            this.updateVisibility();
          });
      }

      handleRelatedProductsResponse(responseText) {
        if (!this.relatedSection) return;

        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const responseTemplate = responseDocument.querySelector(selectors.apiTemplate);

        if (!responseTemplate) {
          this.hideSection(this.relatedSection);
          this.updateVisibility();
          return;
        }

        const responseContainer = document.createElement('div');
        responseContainer.innerHTML = responseTemplate.innerHTML;

        const gridItems = responseContainer.querySelectorAll(selectors.gridItem);
        this.relatedItems = gridItems.length;

        if (this.relatedItems < 1) {
          this.hideSection(this.relatedSection);
          this.updateVisibility();
          return;
        }

        this.relatedSection.innerHTML = responseContainer.innerHTML;

        const firstGridItem = this.relatedSection.querySelector(selectors.gridItem);
        const grid = firstGridItem?.parentElement;
        if (grid) {
          const mobileColumns = parseInt(
            this.relatedSection.style.getPropertyValue('--COLUMNS-MOBILE'),
            10
          );

          grid.classList.toggle(classes.mobileSlider, mobileColumns === 0);
          grid.classList.toggle(
            classes.slider,
            this.relatedSection.getAttribute(attributes.layout) === 'slider'
          );
        }

        this.updateVisibility();
      }

      loadRecentlyViewedProducts() {
        if (!this.recentlyViewed) {
          this.updateVisibility();
          return;
        }

        this.recentlyViewed.addEventListener(
          'theme:recently-viewed:loaded',
          this.recentlyViewedLoadedEvent
        );
      }

      handleRecentlyViewedResponse() {
        if (!this.recentlyViewed) return;

        const minimum = parseInt(this.recentlyViewed.dataset.minimum, 10) || 1;
        const productItems = this.recentlyViewed.querySelectorAll(selectors.productItem);
        const gridSlider = this.recentlyViewed.querySelector(selectors.gridSlider);
        const hasStandaloneItems = !this.wrapper && productItems.length > 0;
        const hasMinimumWrappedItems = Boolean(this.wrapper && productItems.length >= minimum);

        if (hasStandaloneItems || hasMinimumWrappedItems) {
          if (hasMinimumWrappedItems) this.wrapper?.classList.remove(classes.hidden);

          this.recentlyViewed.classList.remove(classes.hidden);
          this.recentlyViewed.dispatchEvent(
            new CustomEvent('theme:tab:check', { bubbles: true })
          );
          gridSlider?.dispatchEvent(
            new CustomEvent('theme:grid-slider:init', { bubbles: true })
          );
        }

        this.updateVisibility();
      }

      hideSection(section) {
        section?.dispatchEvent(new CustomEvent('theme:tab:hide', { bubbles: true }));
      }

      updateVisibility() {
        const productsConfig = window.Shopify?.Products?.getConfig?.();
        if (!productsConfig) return;

        const shouldHide = productsConfig.howManyToShow < 1 && this.relatedItems < 1;
        this.classList.toggle(classes.hidden, shouldHide);
      }
    }
  );
})();
